import { useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';

import { CookieNames, ExchangeOddsTypes, OddsTypes } from 'constants/app';
import { getIsAmericanOddsDefault, getIsAmericanOddsEnabled } from 'redux/modules/appConfigs/selectors';
import { getLoggedInStatusState } from 'redux/modules/auth/selectors';
import { setSettings } from 'redux/modules/user';
import { getAccountSettingsOddsType } from 'redux/modules/user/selectors';
import { ExchangeOddsType } from 'types';

type OddsTypeData = {
  exchangeOddsType: ExchangeOddsType;
  isAmericanOdds: boolean;
  updateOddsType: (oddsType: ExchangeOddsType) => void;
};

const useOddsType = (): OddsTypeData => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(getLoggedInStatusState);
  const isAmericanOddsEnabled = useSelector(getIsAmericanOddsEnabled);
  const isAmericanOddsDefault = useSelector(getIsAmericanOddsDefault);
  const accountOddsType = useSelector(getAccountSettingsOddsType);
  const [cookies, setCookie] = useCookies([CookieNames.EXCHANGE_ODDS_TYPE]);

  const oddsType = useMemo(() => {
    if (isAmericanOddsEnabled) {
      const cookieValue = ExchangeOddsTypes.includes(cookies.EXCHANGE_ODDS_TYPE)
        ? (cookies.EXCHANGE_ODDS_TYPE as ExchangeOddsType)
        : null;

      if (isLoggedIn && accountOddsType) return accountOddsType;
      if (cookieValue) return cookieValue;

      return isAmericanOddsDefault ? OddsTypes.US : OddsTypes.DEC;
    }

    return OddsTypes.DEC;
  }, [accountOddsType, isAmericanOddsEnabled, isAmericanOddsDefault, isLoggedIn, cookies.EXCHANGE_ODDS_TYPE]);

  const updateOddsType = (type: ExchangeOddsType) => {
    setCookie(CookieNames.EXCHANGE_ODDS_TYPE, type, { path: '/' });

    if (isLoggedIn) {
      dispatch(
        setSettings({
          settings: { sportsGamesOddsType: type }
        })
      );
    }
  };

  return {
    exchangeOddsType: oddsType,
    isAmericanOdds: oddsType === OddsTypes.US,
    updateOddsType
  };
};

export default useOddsType;
